import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { BodyMicro } from '#/components/Text/BodyMicro';
import { H4 } from '#/components/Text/H4';
import { useLatest } from '#/hooks/useLatest';
import { useTransitionGroup } from '#/hooks/useTransitionGroup';
import { useElementReachedEffect } from '#/hooks/viewport/useElementReachedEffect';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { I18nLocale } from '#/i18n/types/I18nLocale';
import type { TickElement } from '#/modules/salary/components/Axis/Axis';
import { Axis } from '#/modules/salary/components/Axis/Axis';
import { Line } from '#/modules/salary/components/Line/Line';
import { normalizeDataToPixel } from '#/modules/salary/helpers/normalizeDataToPixel';
import type { CareerPathAnalyticsData } from '#/modules/salary/types/CareerPathAnalyticsData';
import type { CareerPathChartData } from '#/modules/salary/types/CareerPathChartData';
import type { RidgeLineData } from '#/modules/salary/types/RidgeLineData';
import { colors } from '#/theme/theme';
import { trackEventOnce } from '#/utils/analytics/track/trackEventOnce';

import {
  BarContainer,
  CareerPathChartContainer,
  CareerPathChartWrapper,
  ChartDataContainer,
  ChartDataWrapper,
  HorizontalAxisContainer,
  StyledBar,
  TooltipContainer,
} from './CareerPathChart.styled';
import { ModalButton } from './ModalButton';

function formatSalary(salary: number, locale: I18nLocale): string {
  return salary.toLocaleString(locale, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
  });
}

function normalizeMeasuredWidth(width: number): number {
  if (!Number.isFinite(width) || width <= 0) {
    return 0;
  }

  return Math.round(width);
}

type RidgeLineDataWithFormattedRange = RidgeLineData & {
  salaryRangeLower?: string;
  salaryRangeUpper?: string;
};

type TransitionItem = {
  item: RidgeLineData;
  stage: string;
  index: number;
};

type BarRow = {
  index: number;
  key: number;
  name: string;
  stage: string;
  lineProps: {
    chartWidth: number;
    data: RidgeLineData['points'];
    hoverSections: Record<string, { coords: string }>;
    maxX: number;
    maxY: number;
    minX: number;
    minY: number;
  };
  offset: number;
  width: string;
  tooltipLabel: string;
  tooltipOffset: number;
};

type ChartProps = {
  analyticsData: CareerPathAnalyticsData;
  data: CareerPathChartData;
  loading: boolean;
};

export function CareerPathChart({ analyticsData, data, loading }: ChartProps) {
  const { i18n, locale } = useI18n();

  const [chartWidth, setChartWidth] = useState<number>(0);

  const { ridgeLineChart } = data || {};

  const chartRef = useRef<HTMLDivElement>(null);

  const analyticsDataRef = useLatest(analyticsData);
  const analyticsReachIdentity = useMemo(
    () => ({
      job_title_main: analyticsData.job_title_main,
      city: analyticsData.city,
      years_of_experience: analyticsData.years_of_experience,
    }),
    [
      analyticsData.city,
      analyticsData.job_title_main,
      analyticsData.years_of_experience,
    ],
  );

  const setMeasuredWidth = useCallback((width: number) => {
    const normalizedWidth = normalizeMeasuredWidth(width);

    if (!normalizedWidth) {
      return;
    }

    setChartWidth((previousWidth) =>
      previousWidth === normalizedWidth ? previousWidth : normalizedWidth,
    );
  }, []);

  useEffect(() => {
    const chartElement = chartRef.current;

    if (!chartElement) {
      return;
    }

    if (typeof ResizeObserver === 'undefined') {
      setMeasuredWidth(chartElement.getBoundingClientRect().width);
      return;
    }

    const resizeObserver = new ResizeObserver(([entry]) => {
      if (entry) {
        setMeasuredWidth(entry.contentRect.width);
      }
    });

    resizeObserver.observe(chartElement);

    return () => resizeObserver.disconnect();
  }, [setMeasuredWidth]);

  const handleReach = useCallback(() => {
    const currentAnalyticsData = analyticsDataRef.current;

    trackEventOnce('Career Path Report Displayed', {
      ...currentAnalyticsData,
      ...analyticsReachIdentity,
    });
  }, [analyticsDataRef, analyticsReachIdentity]);

  useElementReachedEffect({
    ref: chartRef,
    threshold: 0.5,
    onReach: handleReach,
  });

  const {
    xAxisTicks,
    xGridLines,
    yAxisLabel,
    yGridLines,
    xRangeMin,
    xRangeMax,
  } = ridgeLineChart;

  const plotHeight = 50;
  const numberOfPlots = ridgeLineChart.data.length;
  const chartHeight = plotHeight * numberOfPlots;

  const duration = 500;
  const chartRange = xRangeMax - xRangeMin;

  const transitionFn = useTransitionGroup<RidgeLineData>(
    ridgeLineChart.data,
    duration,
  );

  const transitions = transitionFn<TransitionItem>((item, stage, index) => ({
    item,
    stage,
    index,
  }));

  const animationStages = useMemo(
    () =>
      Object.fromEntries(
        transitions.map(({ item, stage }) => [item.name, stage]),
      ),
    [transitions],
  );

  const yTickComponents = useMemo(
    () =>
      transitions.map<TickElement>(({ item }) => ({
        name: item.name,
        component: <ModalButton item={item} />,
      })),
    [transitions],
  );

  const xAxisTickComponents = useMemo(
    () => xAxisTicks.map((tick) => ({ name: tick, component: tick })),
    [xAxisTicks],
  );

  const xGridInterval = useMemo(
    () => chartWidth / (xGridLines - 1) - 1,
    [chartWidth, xGridLines],
  );

  const yGridInterval = useMemo(
    () => chartHeight / yGridLines,
    [chartHeight, yGridLines],
  );

  const expectedSalaryLabel = i18n('Expected Salary');

  const barRows = useMemo<Array<BarRow | null>>(
    () =>
      chartWidth
        ? transitions.map(({ item, stage, index }) => {
            if (item.count <= 0) {
              return null;
            }

            const salary = item as RidgeLineDataWithFormattedRange;
            const lineWidth = chartWidth * (item.count / chartRange);
            const lowerBound68Coord = normalizeDataToPixel(
              item.sectionLimits.middle68LowerBound,
              item.xRangeMin,
              item.xRangeMax,
              lineWidth,
            );
            const upperBound68Coord = normalizeDataToPixel(
              item.sectionLimits.middle68UpperBound,
              item.xRangeMin,
              item.xRangeMax,
              lineWidth,
            );

            return {
              index,
              key: index * ridgeLineChart.data.length,
              name: item.name,
              stage,
              lineProps: {
                chartWidth: lineWidth,
                data: item.points,
                hoverSections: {
                  [`interval68-${index}`]: {
                    coords: `${lowerBound68Coord} 0, ${lowerBound68Coord} 200, ${upperBound68Coord} 200, ${upperBound68Coord} 0`,
                  },
                },
                maxX: item.xRangeMax,
                maxY: item.yRangeMax,
                minX: item.xRangeMin,
                minY: item.yRangeMin,
              },
              offset:
                ((item.points[0][0] - xRangeMin) / (xRangeMax - xRangeMin)) *
                100,
              width: `${(item.count / chartRange) * 100}%`,
              tooltipLabel: `${
                salary.salaryRangeLower ??
                formatSalary(item.sectionLimits.middle68LowerBound, locale)
              }-${
                salary.salaryRangeUpper ??
                formatSalary(item.sectionLimits.middle68UpperBound, locale)
              }`,
              tooltipOffset:
                (item.salary / (item.xRangeMin + item.xRangeMax)) * 100,
            };
          })
        : [],
    [
      chartRange,
      chartWidth,
      locale,
      ridgeLineChart.data.length,
      transitions,
      xRangeMax,
      xRangeMin,
    ],
  );

  return (
    <CareerPathChartContainer data-qa-id="career-path-chart">
      <CareerPathChartWrapper>
        <Axis
          alignToGrid={false}
          direction="y"
          type="category"
          gridLines={yGridLines}
          tickSpace={10}
          chartWidth={chartHeight}
          animationStages={animationStages}
          duration={500}
          ticks={yTickComponents}
          label={yAxisLabel}
        />
        <ChartDataContainer
          ref={chartRef}
          $chartHeight={chartHeight}
          $loading={loading}
        >
          <ChartDataWrapper
            $xGridInterval={xGridInterval}
            $yGridInterval={yGridInterval}
            $showXGridLines
          >
            {chartWidth
              ? barRows.map((row, index) => {
                  if (row === null) {
                    return (
                      <BarContainer
                        key={index * ridgeLineChart.data.length}
                        $plotHeight={plotHeight}
                      />
                    );
                  }

                  return (
                    <BarContainer
                      data-qa-id="career-path-chart-job"
                      key={row.key}
                      $plotHeight={plotHeight}
                    >
                      <StyledBar
                        $animationDelay={0}
                        $active
                        $loading={false}
                        $duration={duration}
                        $stage={row.stage}
                        component={
                          <Line
                            active
                            fillColor={colors.skyBlue}
                            chartHeight={plotHeight}
                            chartWidth={row.lineProps.chartWidth}
                            hoverSections={row.lineProps.hoverSections}
                            hoverSectionColor={colors.hoverBlue}
                            loading={false}
                            data={row.lineProps.data}
                            initialized
                            minX={row.lineProps.minX}
                            minY={row.lineProps.minY}
                            maxX={row.lineProps.maxX}
                            maxY={row.lineProps.maxY}
                          />
                        }
                        initialized
                        index={row.index}
                        orientation="horizontal"
                        bucketCount={ridgeLineChart.data.length}
                        offset={row.offset}
                        height="100%"
                        width={row.width}
                        tooltip={
                          <TooltipContainer>
                            <BodyMicro opacity={0.8}>
                              {expectedSalaryLabel}
                            </BodyMicro>
                            <H4 mb={0} mt={0}>
                              {row.tooltipLabel}
                            </H4>
                          </TooltipContainer>
                        }
                        showTooltip
                        tooltipColor={colors.brandBlue}
                        tooltipOffset={row.tooltipOffset}
                      />
                    </BarContainer>
                  );
                })
              : null}
          </ChartDataWrapper>
        </ChartDataContainer>
        <HorizontalAxisContainer>
          <Axis
            alignToGrid
            direction="x"
            gridLines={xGridLines - 1}
            chartWidth={chartWidth}
            ticks={xAxisTickComponents}
            type="range"
          />
        </HorizontalAxisContainer>
      </CareerPathChartWrapper>
    </CareerPathChartContainer>
  );
}
